import { Fragment, useEffect, useMemo, useRef } from "react"
import type { FC, MutableRefObject, ReactElement } from "react"

import { AnimatePresence, LazyMotion, domAnimation, m, useReducedMotion } from "framer-motion"
import type { MotionNodeAnimationOptions } from "motion-dom"

import { ESpinnerSize, ESpinnerVariant, Spinner } from "@/3514/components"
import { useIsFetching } from "@/3514/hooks"
import { composeGuards } from "@/3514/utils"
import { type TEmptyRenderCallback } from "@/shared/types/functions"

import { ECoachingChatQueryKey as EQueryKey } from "../config"
import { useCoachingChatContext as useChatContext } from "../context"
import { coachingChatConversationContainerGuards } from "../guards"
import {
    useCoachingChatConversationApiMode as useConversationApiMode,
    useCoachingChatConversationIdSelector as useConversationIdSelector
} from "../store"
import { coachingChatStyles as chatStyles } from "../styles"
import { ECoachingChatConversationApiMode as EConversationApiMode } from "../types"

import { CoachingChatBanner as Banner } from "./coaching-chat.banner.component"
import { CoachingChatError as ErrorBox } from "./coaching-chat.error.component"
import { CoachingChatFeed as Feed } from "./coaching-chat.feed.component"
import { CoachingChatHeading as Heading } from "./coaching-chat.heading.component"
import { CoachingChatInput as Input } from "./coaching-chat.input.component"
import { CoachingChatMessages as Messages } from "./coaching-chat.messages.component"
import { CoachingChatSummary as Summary } from "./coaching-chat.summary.component"
import { CoachingChatTypingIndicator as TypingIndicator } from "./coaching-chat.typing-indicator.component"

const {
    withStatusGuard: withStatus,
    withModeGuard: withMode,
    withDynamicHeightGuard: withDynamicHeight
} = coachingChatConversationContainerGuards

const {
    conversation: { container: classes }
} = chatStyles

function renderHeading(): ReactElement {
    return <Heading />
}
function renderBanner(): ReactElement {
    return <Banner />
}
function renderTypingIndicator(isVisible: boolean): ReactElement {
    return isVisible ? <TypingIndicator isDimmed /> : null
}
function renderMessages(): ReactElement {
    return <Messages />
}
function renderErrorBox(): ReactElement {
    return <ErrorBox />
}
function renderInput(): ReactElement {
    return <Input />
}
function renderSummary(): ReactElement {
    return <Summary />
}
function renderFeed(): ReactElement {
    return (
        <Feed
            renderTypingIndicator={renderTypingIndicator}
            renderMessages={renderMessages}
            renderErrorBox={renderErrorBox}
            renderSummary={renderSummary}
        />
    )
}

const Conversation: FC = (): ReactElement => {
    const isMotionReduced: boolean = useReducedMotion()

    const spinnerMotionProps: MotionNodeAnimationOptions = useMemo(
        (): MotionNodeAnimationOptions =>
            isMotionReduced
                ? {
                      initial: { opacity: 0 },
                      animate: { opacity: 1, transition: { duration: 0 } },
                      exit: { opacity: 0, transition: { duration: 0 } }
                  }
                : {
                      initial: { opacity: 0, y: 6 },
                      animate: { opacity: 1, y: 0, transition: { duration: 0.18, ease: [0.16, 1, 0.3, 1] } },
                      exit: { opacity: 0, y: 6, transition: { duration: 0.12, ease: [0.4, 0, 1, 1] } }
                  },
        [isMotionReduced]
    )

    const {
        entityId,
        conversation: { ref }
    }: ReturnType<typeof useChatContext> = useChatContext()

    const conversationId: number | null = useConversationIdSelector(entityId)

    const connectionApiMode: EConversationApiMode = useConversationApiMode()

    const isConversationFetching: boolean = useIsFetching(EQueryKey.Conversation(conversationId))

    const presentedConversationIdsRef: MutableRefObject<Set<number | null>> = useRef<Set<number | null>>(new Set())

    const wasConversationPresented: boolean = presentedConversationIdsRef.current.has(conversationId)
    const isConversationLoading: boolean =
        connectionApiMode === null || (isConversationFetching && !wasConversationPresented)

    useEffect((): void => {
        if (!isConversationFetching && connectionApiMode !== null)
            presentedConversationIdsRef.current.add(conversationId)
    }, [connectionApiMode, conversationId, isConversationFetching])

    const isChatLocked: boolean = isConversationLoading

    const renderSpinner: TEmptyRenderCallback = (): ReactElement => (
        <m.div key="loading" {...spinnerMotionProps} className="my-auto">
            <Spinner variant={ESpinnerVariant.White} size={ESpinnerSize.Large} wrapperClassName={classes.spinner} />
        </m.div>
    )

    return (
        <LazyMotion features={domAnimation}>
            <m.div ref={ref} className={classes.container({ isChatLocked })} layout="position">
                {renderBanner()}

                {renderHeading()}

                <AnimatePresence mode="wait" initial={false}>
                    {isConversationLoading ? (
                        <Fragment key="loading-state">{renderSpinner()}</Fragment>
                    ) : (
                        <Fragment key="ready-state">
                            {renderFeed()}

                            {renderInput()}
                        </Fragment>
                    )}
                </AnimatePresence>
            </m.div>
        </LazyMotion>
    )
}

const GuardedChat: FC = composeGuards<{}>(withMode, withStatus, withDynamicHeight)(Conversation) as FC

GuardedChat.displayName = "CoachingChatConversation"

export { GuardedChat as CoachingChatConversation }
